import Alert from "@mui/material/Alert";
import { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePanelUpdate } from "../data/Assets/usePanelUpdate";
import { CancelButton } from "../shared/CancelButton";
import { getSubAssetType, Panel } from "../shared/interfaces/SurveyableAsset";
import { ProgressButton } from "../shared/ProgressButton";
import { Snackbar } from "../shared/Snackbar";

interface Props {
  visible: boolean;
  onSuccess: (panelNumber: string, name: string) => void;
  onClose: () => void;
  existingPanelDetails: Panel;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "nowrap",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(0),
      width: "100%",
    },
    formLabel: {
      marginRight: theme.spacing(4),
      fontSize: "14px",
    },
    formValue: {
      marginRight: theme.spacing(2),
      fontSize: "14px",
      width: "60%",
    },
    inputText: {
      width: "100%",
    },
  }),
);

export function UpdatePanelNameDialog(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { visible, onSuccess, onClose, existingPanelDetails } = props;

  const [panelName, setPanelName] = useState<string>(existingPanelDetails.name || "");

  const {
    mutate: updatePanelName,
    isError: errorUpdatingPanelName,
    isLoading: panelNameUpdateLoading,
    isSuccess: panelNameSuccessfullyUpdated,
    reset: resetPanelNameUpdate,
  } = usePanelUpdate();

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  function handleCloseSnackbar() {
    resetPanelNameUpdate();
    setShowSnackbar(false);
  }

  const subAssetLabel = getSubAssetType(existingPanelDetails.assetType);

  useEffect(() => {
    if (errorUpdatingPanelName) {
      setSnackbarText(t("common.dataError", { val: t("panels.updatePanel", { val: subAssetLabel }) }));
      setShowSnackbar(true);
    } else {
      setShowSnackbar(false);
    }
  }, [errorUpdatingPanelName, t, subAssetLabel]);

  useEffect(() => {
    if (panelNameSuccessfullyUpdated) {
      resetPanelNameUpdate();
      onSuccess(existingPanelDetails.panelNo, panelName);
    }
  }, [existingPanelDetails.panelNo, onSuccess, panelName, panelNameSuccessfullyUpdated, resetPanelNameUpdate]);

  const update = useCallback(() => {
    const panelSubmission: Panel = {
      ...existingPanelDetails,
      name: panelName,
    };
    if (panelSubmission.panelAssetId == null) {
      setSnackbarText(t("common.dataError", { val: t("panels.updatePanel", { val: subAssetLabel }) }));
      setShowSnackbar(true);
    } else {
      updatePanelName(panelSubmission);
    }
  }, [existingPanelDetails, panelName, t, updatePanelName, subAssetLabel]);

  function handlePanelNameChange(event: any) {
    setPanelName(event.target.value);
  }

  const header = (
    <Typography variant="h5" component="p">
      {t("panels.updatePanelName", { val: subAssetLabel })}
    </Typography>
  );

  const footer = (
    <React.Fragment>
      <ProgressButton disabled={panelName.length === 0} loading={panelNameUpdateLoading} onClick={update}>
        {t("common.save")}{" "}
      </ProgressButton>
      <CancelButton onClick={onClose} className="m1-1" />
    </React.Fragment>
  );

  return visible ? (
    <>
      <Dialog
        visible={visible}
        header={header}
        footer={footer}
        onHide={onClose}
        breakpoints={{
          "2000px": "35vw",
          "1300px": "50vw",
          "900px": "75vw",
          "640px": "98vw",
        }}
        style={{ width: "20vw" }}
      >
        <div className={classes.formControl}>
          <Typography className={classes.formLabel} variant="subtitle2">
            {t("panels.panelName", {
              val: subAssetLabel,
            })}
          </Typography>
          <div className={classes.formValue}>
            <InputText
              className={classes.inputText}
              value={panelName}
              onChange={handlePanelNameChange}
              placeholder={t("panels.panelName", {
                val: subAssetLabel,
              })}
              maxLength={50}
            />
          </div>
        </div>
      </Dialog>
      <Snackbar open={showSnackbar} onClose={handleCloseSnackbar} message={snackbarText}>
        {errorUpdatingPanelName || existingPanelDetails.panelAssetId == null ? (
          <Alert severity="error" variant="filled">
            {snackbarText}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  ) : null;
}
